// apiHandler.jsx
import axios from 'axios';
import { API_BASE_URL } from './../Api/apiconnector';
// Create an axios instance with default settings
const api = axios.create({
  baseURL: API_BASE_URL, // use same origin for session cookie
  withCredentials: true, // sends cookies automatically
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // ADD THIS LINE — 30s default timeout for all requests
});

class ApiHandler {
  // Generic method for GET/POST/PUT/DELETE requests
  static async makeRequest(method, endpoint, data = null, config = {}) {
    try {
      const response = await api.request({
        url: endpoint,
        method,
        data,
        ...config,
      });
      return { success: true, data: response.data, status: response.status };
    } catch (error) {
      console.error('API Request failed:', error.response?.data || error.message);
      return {
        success: false,
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
  }

  // Login user
  static async login(username, password) {
    return await this.makeRequest('POST', '/login/', { username, password });
  }

  // Logout user
  static async logout() {
    return await this.makeRequest('POST', '/logout/');
  }

  // Get all patient DICOM reports with cursor-based pagination
  static async getTatCounters(cursor = null) {
    const params = new URLSearchParams();
    params.append('limit', '400'); // Adjust limit as needed
    if (cursor) {
      params.append('cursor', cursor);
    }
    return await this.makeRequest('GET', `/fetch-tat-counters/?${params.toString()}`);
  }

  // Update a patient DICOM report
  static async updateDicomReport(id, data) {
    return await this.makeRequest('PUT', `/update-dicom/${id}/`, data);
  }

  // Get body parts
  static async getBodyParts() {
    const response = await this.makeRequest('GET', '/body-parts/');
    if (response.success && Array.isArray(response.data)) {
      response.data.sort((a, b) => a.name.localeCompare(b.name));
    }
    return response;
  }


  // Get presigned URL for file download
  static async getPresignedUrl(fileKey) {
    try {
      const response = await api.post('/get-file-url/', { key: fileKey });
      return { success: true, url: response.data.url };
    } catch (error) {
      console.error('Error fetching presigned URL:', error.response?.data || error.message);
      return { 
        success: false, 
        error: error.response?.data || error.message 
      };
    }
  }

  static async getCompletePresignedUrl(fileKey) {
    try {
      const response = await api.post('/get-file-url-complete/', { key: fileKey });
      return { success: true, url: response.data.url };
    } catch (error) {
      console.error('Error fetching presigned URL:', error.response?.data || error.message);
      return { 
        success: false, 
        error: error.response?.data || error.message 
      };
    }
  }

  // Download a report file (PDF/Word)
  // Download a report file using presigned URL
static async downloadReport(fileKey, fileName = 'report.pdf') {
  try {
    // First, get the presigned URL
    const urlResult = await ApiHandler.getPresignedUrl(fileKey);
    
    if (!urlResult.success || !urlResult.url) {
      throw new Error('Failed to get download URL');
    }

    // Download the file using the presigned URL
    const response = await fetch(urlResult.url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, message: 'File downloaded successfully' };
  } catch (error) {
    console.error('Download failed:', error);
    return { success: false, error: error.message };
  }
}

  // Get FAQs
  static async getFaqs(group) {
    return await this.makeRequest('GET', `/faqs/?group=${group}`);
  }

  // Upload history file
  // static async uploadHistoryFile(dicomId, historyFiles) {
  //   const formData = new FormData();
  //   formData.append('dicom_data', dicomId);

  //   if (Array.isArray(historyFiles)) {
  //     historyFiles.forEach((file) => formData.append('history_file', file));
  //   } else if (historyFiles) {
  //     formData.append('history_file', historyFiles);
  //   }

  //   return await this.makeRequest('POST', `/upload-historyfile/${dicomId}/`, formData, {
  //     headers: { 'Content-Type': 'multipart/form-data' },
  //   });
  // }     

  // Upload history file
  static async uploadHistoryFile(dicomId, historyFiles) {
    const formData = new FormData();
    formData.append('dicom_data', dicomId);

    if (Array.isArray(historyFiles)) {
      historyFiles.forEach((file) => formData.append('history_file', file));
    } else if (historyFiles) {
      formData.append('history_file', historyFiles);
    }

    return await this.makeRequest('POST', `/upload-historyfile/${dicomId}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000, // 90s — file uploads need more time than the default
    });
  }

  
  static async fetchCompletedTatCounters(limit = 200, cursor = null){
    try {
      const url = new URL(`${API_BASE_URL}/fetch-completed-tat-counters/`);
      url.searchParams.append('limit', limit);
      if (cursor) {
        url.searchParams.append('cursor', cursor);
      }
      // Log the full request URL to help debug when the API is (or isn't) being called
      console.log('Fetching completed TAT counters from:', url.toString());

      // Include credentials (cookies) in case the backend requires session auth
      const response = await fetch(url, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Trigger backend to email a PDF for a given patient id.
  // Endpoint: POST /email-pdf-raw/<patient_id>/
  // Payload: { email, name, pdf_url }
  static async sendEmailPdfRaw(patientId, payload) {
    if (!patientId) {
      return { success: false, error: 'Missing patientId', status: 400 };
    }
    return await this.makeRequest('POST', `/email-pdf-raw/${encodeURIComponent(patientId)}/`, payload);
  }

  // Trigger backend to send WhatsApp message with PDF.
  // Endpoint: POST /send-whatsapp/
  // Payload: { whatsapp_number, patient_name, pdf_url, patient_id }
  static async sendWhatsapp(payload) {
    return await this.makeRequest('POST', `/send-whatsapp/`, payload);
  }



  static async searchPatients(searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    return { success: true, data: [] };
  }

  // Trim the search term
  const trimmedTerm = searchTerm.trim();
  console.log("Search term:", trimmedTerm); // Debug log

  // Check patterns
  const isAlphabetic = /^[A-Za-z ]+$/.test(trimmedTerm);
  const isAlphaNumeric = /^[A-Za-z0-9]+$/.test(trimmedTerm);
  const hasNumbers = /\d/.test(trimmedTerm);

  console.log("Is Alphabetic:", isAlphabetic); // Debug log
  console.log("Is AlphaNumeric:", isAlphaNumeric); // Debug log
  console.log("Has Numbers:", hasNumbers); // Debug log

  // Build URL based on condition
  let endpoint = "";

  if (isAlphabetic) {
    // Name search
    endpoint = `/search_patient/?patient_name=${encodeURIComponent(trimmedTerm)}`;
  } else if (isAlphaNumeric || hasNumbers) {
    // ID search
    endpoint = `/search_patient/?patient_id=${encodeURIComponent(trimmedTerm)}`;
  } else {
    // Contains special characters → skip
    return { success: true, data: [] };
  }

  console.log("API Endpoint:", endpoint); // Debug log

  try {
    console.log("Making API call to:", `${API_BASE_URL}${endpoint}`); // Debug log

    // ✅ Send credentials for Django session auth
    const response = await api.get(endpoint, { withCredentials: true });

    console.log("API Response:", response); // Debug log

    return {
      success: true,
      data: response.data.results || response.data || [],
      status: response.status
    };
  } catch (error) {
    console.error("Error searching patients:", error.response?.data || error.message);

    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status || 500,
    };
  }
}






static async searchReport(searchTerm) {
  if (!searchTerm || searchTerm.trim() === '') {
    return { success: true, data: [] };
  }

  // Trim the search term
  const trimmedTerm = searchTerm.trim();
  
  console.log("Search term:", trimmedTerm); // Debug log

  // Check patterns - UPDATED LOGIC
  const isAlphabetic = /^[A-Za-z ]+$/.test(trimmedTerm);
  const hasNumbers = /\d/.test(trimmedTerm); // Contains any numbers
  
  console.log("Is Alphabetic:", isAlphabetic); // Debug log
  console.log("Has Numbers:", hasNumbers); // Debug log

  // Build URL based on condition
  let endpoint = "";

  if (isAlphabetic) {
    // Pure alphabetic - search by name
    endpoint = `/search_report/?patient_name=${encodeURIComponent(trimmedTerm)}`;
  } else if (hasNumbers) {
    // Contains numbers - search by ID (allows hyphens, numbers, letters)
    endpoint = `/search_report/?patient_id=${encodeURIComponent(trimmedTerm)}`;
  } else {
    // Special characters only - no results
    return { success: true, data: [] };
  }

  console.log("API Endpoint:", endpoint); // Debug log

  try {
    console.log("Making API call to:", `${API_BASE_URL}${endpoint}`); // Debug log
    const response = await api.get(endpoint, { withCredentials: true });
    console.log("API Response:", response); // Debug log
    
    return { 
      success: true, 
      data: response.data.results || response.data || [],
      status: response.status 
    };
  } catch (error) {
    console.error("Error searching reports:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data || error.message,
      status: error.response?.status || 500,
    };
  }
}
  
  


}

export default ApiHandler;





